<template>
    <div class="l-org-chart">
        <div id="chart"></div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import escapeHTML from "escape-html";
import * as d3 from "d3";

import generateDefaultProfilePicture from "@/utils/generate-default-profile-picture";
import generateFallbackPictureText from "@/utils/generate-fallback-picture-text";

const DEFAULT_PICTURE = "/img/icons/user_small.png";

d3.selection.prototype.patternify = function(params) {
    var container = this;
    var selector = params.selector;
    var elementTag = params.tag;
    var data = params.data || [selector];

    // Pattern in action
    var selection = container.selectAll("." + selector).data(data, (d, i) => {
        if (typeof d === "object") {
            if (d.id) {
                return d.id;
            }
        }
        return i;
    });
    selection.exit().remove();
    selection = selection
        .enter()
        .append(elementTag)
        .merge(selection);
    selection.attr("class", selector);
    return selection;
};

export default {
    components: {},
    props: {
        data: {
            type: Object,
            required: true,
        },
        config: {
            type: Object,
            default: () => ({}),
        },
    },
    computed: {
        ...mapGetters({
            user: "users/current/user",
        }),
    },
    watch: {
        config: {
            handler(c) {
                if (c.zoom > 0) {
                    // Scale chart
                    this.attrs.centerG.attr(
                        "transform",
                        `translate(${this.attrs.chartWidth / 2},${-1 *
                            c.zoom *
                            this.attrs.nodeMaxHeight}) scale(${c.zoom})`,
                    );

                    // Resize SVG
                    const height = Math.max(
                        window.innerHeight - 350,
                        this.attrs.chart.node().getBBox().height + 20,
                    );
                    this.attrs.svg.attr("height", height);
                }
            },
        },
    },
    created() {
        const defaultAttrs = {
            svgWidth: 800,
            svgHeight: window.innerHeight - 350,
            marginTop: 0,
            marginBottom: 0,
            marginRight: 0,
            marginLeft: 0,
            buttonRadius: 8,
            container: "#chart",
            defaultTextFill: "#424242",
            defaultFont: "Circular, Roboto, sans-serif",
            backgroundColor: "#ffffff",
            data: null,
            duration: 0,
            strokeWidth: 1,
            initialZoom: this.config.zoom || 1,
            onNodeClick: userId => {
                if (userId !== "root") {
                    this.$router.push(`/profile/${userId}`);
                }
            },
        };

        // Add config overrides to attributes
        this.attrs = { ...defaultAttrs, ...this.config };

        // Transform data
        this.attrs.data = this.formatData();
    },
    mounted() {
        this.render();
    },
    methods: {
        // Heavily inspired on: https://github.com/bumbeishvili/d3-organization-chart
        //
        formatData() {
            const formattedData = d3
                .hierarchy(this.data)
                .descendants()
                .map(d => Object.assign(d, { id: d.data._id }))
                .map(d =>
                    Object.assign(d.data, {
                        id: d.id,
                        parentId: d.parent && d.parent.id,
                    }),
                )
                .map((d, index, nodes) => {
                    // When there are multiple "roots", we add the company and it's vision as the single root
                    const isCompany = d.id === "root";
                    const isCurrentUser = d.id === this.user._id;

                    // Identify the first child of each branch
                    const isFirstChild =
                        index === 0 || index === 1
                            ? true
                            : d.parentId !== nodes[index - 1].parentId;

                    // Dimensions
                    const nodeImageWidth = 40;
                    const nodeImageHeight = 40;
                    const width = isCompany
                        ? Math.max(160, Math.max(d.name?.length ?? 0, d.title?.length ?? 0) * 7) +
                          nodeImageWidth
                        : nodeImageWidth + 160;
                    const height = nodeImageHeight + 20;

                    // Picture
                    d.picture =
                        d.picture && d.picture !== DEFAULT_PICTURE
                            ? d.picture
                            : generateDefaultProfilePicture(generateFallbackPictureText(d));

                    return {
                        nodeId: d.id,
                        parentNodeId: d.parentId,
                        isCompany,
                        isFirstChild,
                        index,
                        width,
                        height,
                        borderWidth: 0.6,
                        borderRadius: 10,
                        borderColor: `#dadada`,
                        backgroundColor: "#ffffff",
                        userId: d._id,
                        template: `
                            <div class="node-wrapper
                                ${isCurrentUser ? "is-current-user" : ""}
                                ${isCompany ? "is-company" : ""}
                            ">
                                <img
                                    src="${escapeHTML(d.picture)}"
                                    alt="${escapeHTML(d.name)}"
                                    width="${nodeImageWidth}"
                                    height="${nodeImageHeight}"
                                    class="profile-picture"
                                />
                                <div class="profile-info" style="max-width: ${width -
                                    nodeImageWidth -
                                    35}px;">
                                    <div class="profile-info-name ellipsis">
                                        ${escapeHTML(d.name)}
                                    </div>

                                    <div class="profile-info-title ellipsis">
                                        ${d.title ? escapeHTML(d.title) : ""}
                                    </div>
                                </div>
                            </div>`,
                        connectorLineColor: `#dadada`,
                        connectorLineWidth: 1,
                        dashArray: "",
                    };
                });

            return formattedData || [];
        },

        render() {
            //Drawing containers
            const container = d3.select(this.attrs.container);
            if (!container || !container.node()) {
                return;
            }
            const containerRect = container.node().getBoundingClientRect();
            if (containerRect.width > 0) this.attrs.svgWidth = containerRect.width - 2;

            //Calculated properties
            this.attrs.chartLeftMargin = this.attrs.marginLeft;
            this.attrs.chartTopMargin = this.attrs.marginTop;
            this.attrs.chartWidth =
                this.attrs.svgWidth - this.attrs.marginRight - this.attrs.chartLeftMargin;
            this.attrs.chartHeight =
                this.attrs.svgHeight - this.attrs.marginBottom - this.attrs.chartTopMargin;

            // Get maximum node width and height (except the "company" node as it is dynamic and other nodes are static)
            this.attrs.nodeMaxWidth = this.attrs.data.filter(d => !d.isCompany)[0].width;
            this.attrs.nodeMaxHeight = this.attrs.data.filter(d => !d.isCompany)[0].height;

            // Calculate max node depth (it's needed for layout heights calculation)
            this.attrs.depth = this.attrs.nodeMaxHeight + 30;

            //********************  LAYOUTS  ***********************
            const layouts = {
                tree: null,
                collapsible: null,
            };
            this.attrs.layouts = layouts;

            // Generate tree layout function
            layouts.tree = d3
                .tree()
                .size([this.attrs.chartWidth, this.attrs.chartHeight])
                .nodeSize([
                    this.attrs.nodeMaxWidth + 100,
                    this.attrs.nodeMaxHeight + this.attrs.depth,
                ]);

            // Generate collapsible tree layout function
            // From: https://observablehq.com/@paulosobrinho/collapsible-org-chart
            layouts.collapsible = root => {
                const loDesc = [];
                const delta = [];
                let j = 0;

                root.eachBefore(node => {
                    if (node.depth === 1) {
                        loDesc[j] = 0;
                        delta[j] = 0;
                        j++;
                    }
                    if (node.depth > 1) {
                        loDesc[j - 1] = Math.max(loDesc[j - 1], node.depth - 1);
                    }
                });

                if (loDesc.length % 2 !== 0) {
                    delta[Math.floor(loDesc.length / 2)] = 0;
                }

                for (let m = Math.floor(loDesc.length / 2) - 1; m >= 0; m--) {
                    if (m === Math.floor(loDesc.length / 2) - 1) {
                        delta[m] = loDesc[m] * -1;
                    }
                    delta[m] = delta[m + 1] + loDesc[m] * -1;
                }

                for (let n = Math.floor(loDesc.length / 2) + 1; n < loDesc.length; n++) {
                    if (n === Math.floor(loDesc.length / 2) + 1) {
                        delta[n] = loDesc[n - 1];
                    }
                    delta[n] = delta[n - 1] + loDesc[n - 1];
                }

                let last = root;

                root.eachBefore(node => {
                    setPosition({
                        node,
                        last,
                        delta,
                        nodeWidth: this.attrs.nodeMaxWidth,
                        nodeHeight: this.attrs.nodeMaxHeight,
                        marginX:
                            node.depth === 1
                                ? this.attrs.nodeMaxWidth / 6
                                : this.attrs.nodeMaxWidth / 12,
                        marginY:
                            node.depth === 1
                                ? this.attrs.nodeMaxHeight / 2
                                : this.attrs.nodeMaxHeight / 4,
                    });
                    last = node;
                });

                function setPosition({
                    node,
                    last,
                    delta,
                    nodeWidth,
                    nodeHeight,
                    marginX,
                    marginY,
                }) {
                    if (node.depth === 0) {
                        node.x = 0;
                        node.y = 100;
                    } else if (node.depth === 1) {
                        node.x =
                            node.parent.x +
                            (2 * node.data.index - 1 - delta.length) * (nodeWidth / 2 + marginX) +
                            delta[node.data.index - 1];
                        node.y = node.parent.y + nodeHeight + marginY;
                    } else if (node.depth === 2) {
                        node.x = node.parent.x;

                        // We identify the first child at depth 2 as we need extra space on top for the line
                        // to be drawn.
                        //
                        node.y = node.data.isFirstChild
                            ? last.y + 1.25 * nodeHeight + marginY
                            : last.y + nodeHeight + marginY;
                    } else {
                        node.x = node.parent.x + marginX;
                        node.y = last.y + nodeHeight + marginY;
                    }
                }

                return root;
            };

            // ******************* BEHAVIORS **********************
            const behaviors = {
                zoom: null,
            };

            // Get zooming function
            behaviors.zoom = d3.zoom().on("zoom", this.zoomed);

            //****************** ROOT node work ************************

            // Convert flat data to hierarchical
            this.attrs.root = d3
                .stratify()
                .id(({ nodeId }) => nodeId)
                .parentId(({ parentNodeId }) => parentNodeId)(this.attrs.data);

            // Set child nodes enter appearance positions
            this.attrs.root.x0 = 0;
            this.attrs.root.y0 = 0;

            // Get all nodes as array (with extended parent & children properties set)
            this.attrs.allNodes = this.attrs.layouts.tree(this.attrs.root).descendants();

            // Collapse all children at first
            this.attrs.root.children.forEach(d => this.collapse(d));

            // Expand the nodes at depth <= 2
            this.attrs.allNodes
                .filter(d => d.depth <= 2)
                .forEach(d => {
                    d.data.expanded = true;
                });

            // Then expand some nodes, which have `expanded` property set
            this.attrs.root.children.forEach(d => this.expandSomeNodes(d));

            // *************************  DRAWING **************************
            //Add svg
            const svg = container
                .patternify({
                    tag: "svg",
                    selector: "svg-chart-container",
                })
                .attr("width", this.attrs.svgWidth)
                .attr("height", this.attrs.svgHeight)
                .attr("font-family", this.attrs.defaultFont)
                .call(behaviors.zoom)
                .on("wheel.zoom", null)
                .attr("cursor", "move")
                .style("background-color", this.attrs.backgroundColor);

            this.attrs.svg = svg;

            // Add defs
            const defs = svg.patternify({ tag: "defs", selector: "svg-defs" });

            const filterBoxShadow = defs
                .patternify({
                    tag: "filter",
                    selector: "svg-defs-filter-box-shadow",
                })
                .attr("id", "box-shadow");

            filterBoxShadow
                .patternify({
                    tag: "feDropShadow",
                    selector: "svg-defs-filter-box-shadow-drop-shadow",
                })
                .attr("dx", 2)
                .attr("dy", 2)
                .attr("stdDeviation", 2)
                .attr("flood-color", "#000")
                .attr("flood-opacity", 0.1);

            this.attrs.defs = defs;

            //Add container g element
            const chart = svg
                .patternify({
                    tag: "g",
                    selector: "chart",
                })
                .attr(
                    "transform",
                    `translate(${this.attrs.chartLeftMargin},${this.attrs.chartTopMargin})`,
                );

            // Add one more container g element, for better positioning controls
            this.attrs.centerG = chart
                .patternify({
                    tag: "g",
                    selector: "center-group",
                })
                .attr(
                    "transform",
                    `translate(${this.attrs.chartWidth / 2},${-1 *
                        this.attrs.initialZoom *
                        this.attrs.nodeMaxHeight}) scale(${this.attrs.initialZoom})`,
                );

            this.attrs.chart = chart;

            // Display tree contenrs
            this.update(this.attrs.root);
        },

        // This function basically redraws visible graph, based on nodes state
        update({ x0, y0, x, y }) {
            //  Assigns the x and y position for the nodes
            const treeData = this.attrs.layouts.collapsible(this.attrs.root);

            // Get tree nodes and links and attach some properties
            const nodes = treeData.descendants().map(d => {
                // If at least one property is already set, then we don't want to reset other properties
                if (d.width) return d;

                // Extend node object with calculated properties
                return Object.assign(d, {
                    borderColor: d.data.borderColor,
                    backgroundColor: d.data.backgroundColor,
                    width: d.data.width,
                    height: d.data.height,
                });
            });

            // Get all links
            const links = treeData.descendants().slice(1);

            // --------------------------  LINKS ----------------------
            // Get links selection
            const linkSelection = this.attrs.centerG
                .selectAll("path.link")
                .data(links, ({ id }) => id);

            // Enter any new links at the parent's previous position.
            const linkEnter = linkSelection
                .enter()
                .insert("path", "g")
                .attr("class", "link")
                .attr("d", () => {
                    const o = {
                        x: x0,
                        y: y0,
                    };
                    return this.diagonal(o, o);
                });

            // Get links update selection
            const linkUpdate = linkEnter.merge(linkSelection);

            // Styling links
            linkUpdate
                .attr("fill", "none")
                .attr("stroke-width", ({ data }) => data.connectorLineWidth)
                .attr("stroke", ({ data }) => data.connectorLineColor)
                .attr("stroke-dasharray", ({ data }) => data.dashArray);

            // Transition back to the parent element position
            linkUpdate
                .transition()
                .duration(this.attrs.duration)
                .attr("d", d => this.diagonal(d, d.parent));

            // Remove any  links which is exiting after animation
            linkSelection
                .exit()
                .transition()
                .duration(this.attrs.duration)
                .attr("d", () => {
                    const o = {
                        x: x,
                        y: y,
                    };
                    return this.diagonal(o, o);
                })
                .remove();

            // --------------------------  NODES ----------------------
            // Get nodes selection
            const nodesSelection = this.attrs.centerG
                .selectAll("g.node")
                .data(nodes, ({ id }) => id);

            // Enter any new nodes at the parent's previous position.
            const nodeEnter = nodesSelection
                .enter()
                .append("g")
                .attr("class", "node")
                .attr("transform", () => `translate(${x0},${y0})`)
                .attr("cursor", "pointer")
                .on("click", (event, { data }) => {
                    let classes = event?.target?.classList || [];
                    // convert "DOMTokenList" to array
                    classes = [...classes];

                    if (classes.includes("node-button-circle")) return;

                    this.attrs.onNodeClick(data.userId);
                });

            // Add background rectangle for the nodes
            nodeEnter
                .patternify({
                    tag: "rect",
                    selector: "node-rect",
                    data: d => [d],
                })
                .style("fill", ({ _children }) => (_children ? "lightsteelblue" : "#fff"))
                .style("filter", "url(#box-shadow)");

            // Node update styles
            const nodeUpdate = nodeEnter.merge(nodesSelection).style("font", "12px");

            // Add foreignObject element inside rectangle
            const fo = nodeUpdate.patternify({
                tag: "foreignObject",
                selector: "node-foreign-object",
                data: d => [d],
            });

            // Add foreign object
            fo.patternify({
                tag: "xhtml:div",
                selector: "node-foreign-object-div",
                data: d => [d],
            });

            this.restyleForeignObjectElements();

            // Add Node button circle's group (expand-collapse button)
            const nodeButtonGroups = nodeEnter
                .patternify({
                    tag: "g",
                    selector: "node-button-g",
                    data: d => [d],
                })
                .on("click", this.onButtonClick);

            // Add expand collapse button circle
            nodeButtonGroups.patternify({
                tag: "circle",
                selector: "node-button-circle",
                data: d => [d],
            });

            // Add button text
            nodeButtonGroups
                .patternify({
                    tag: "text",
                    selector: "node-button-text",
                    data: d => [d],
                })
                .attr("pointer-events", "none");

            // Transition to the proper position for the node
            nodeUpdate
                .transition()
                .attr("opacity", 0)
                .duration(this.attrs.duration)
                .attr("transform", ({ x, y }) => `translate(${x},${y})`)
                .attr("opacity", 1);

            // Style node rectangles
            nodeUpdate
                .select(".node-rect")
                .attr("width", ({ data }) => data.width)
                .attr("height", ({ data }) => data.height)
                .attr("x", ({ data }) => -data.width / 2)
                .attr("y", ({ data }) => -data.height / 2)
                .attr("rx", ({ data }) => data.borderRadius || 0)
                .attr("cursor", "pointer")
                .style("fill", ({ backgroundColor }) => backgroundColor);

            // Move node button group to the desired position
            nodeUpdate
                .select(".node-button-g")
                .attr("transform", ({ data }) => `translate(0,${data.height / 2})`)
                .attr("opacity", ({ children, _children }) => {
                    if (children || _children) {
                        return 1;
                    }
                    return 0;
                });

            // Restyle node button circle
            nodeUpdate
                .select(".node-button-circle")
                .attr("r", this.attrs.buttonRadius)
                .attr("stroke-width", ({ data }) => data.borderWidth || this.attrs.strokeWidth)
                .attr("fill", this.attrs.backgroundColor)
                .attr("stroke", ({ borderColor }) => borderColor);

            // Restyle button texts
            nodeUpdate
                .select(".node-button-text")
                .attr("text-anchor", "middle")
                .attr("alignment-baseline", "middle")
                .attr("fill", this.attrs.defaultTextFill)
                .attr("font-size", this.attrs.buttonRadius * 2)
                .text(({ children }) => {
                    if (children) return "-";
                    return "+";
                })
                .attr("y", 0);

            // Remove any exiting nodes after transition
            const nodeExitTransition = nodesSelection
                .exit()
                .attr("opacity", 1)
                .transition()
                .duration(this.attrs.duration)
                .attr("transform", () => `translate(${x},${y})`)
                .on("end", function() {
                    d3.select(this).remove();
                })
                .attr("opacity", 0);

            // On exit reduce the node rects size to 0
            nodeExitTransition
                .selectAll(".node-rect")
                .attr("width", 10)
                .attr("height", 10)
                .attr("x", 0)
                .attr("y", 0);

            // Store the old positions for transition.
            nodes.forEach(d => {
                d.x0 = d.x;
                d.y0 = d.y;
            });

            // Resize SVG height
            setTimeout(() => {
                const height = Math.max(
                    window.innerHeight - 350,
                    this.attrs.chart.node().getBBox().height + 20,
                );
                this.attrs.svg.attr("height", height);
            }, this.attrs.duration);

            // Return nodes
            return nodes;
        },

        restyleForeignObjectElements() {
            this.attrs.svg
                .selectAll(".node-foreign-object")
                .attr("width", ({ width }) => width)
                .attr("height", ({ height }) => height)
                .attr("x", ({ width }) => -width / 2)
                .attr("y", ({ height }) => -height / 2);
            this.attrs.svg
                .selectAll(".node-foreign-object-div")
                .style("width", ({ width }) => `${width}px`)
                .style("height", ({ height }) => `${height}px`)
                .style("color", "white")
                .html(({ data }) => data.template);
        },

        // This function changes `expanded` property to descendants
        setExpansionFlagToChildren({ data, children, _children }, flag) {
            // Set flag to the current property
            data.expanded = flag;

            // Loop over and recursively update expanded children's descendants
            if (children) {
                children.forEach(d => {
                    this.setExpansionFlagToChildren(d, flag);
                });
            }

            // Loop over and recursively update collapsed children's descendants
            if (_children) {
                _children.forEach(d => {
                    this.setExpansionFlagToChildren(d, flag);
                });
            }
        },

        // Toggle children on click.
        onButtonClick(_, d) {
            // If childrens are expanded
            if (d.children) {
                //Collapse them
                d._children = d.children;
                d.children = null;

                // Set descendants expanded property to false
                this.setExpansionFlagToChildren(d, false);
            } else {
                // Expand children
                d.children = d._children;
                d._children = null;

                // Set each children as expanded
                d.children.forEach(({ data }) => (data.expanded = true));
            }

            // Redraw Graph
            this.update(d);
        },

        // Method which only expands nodes, which have property set "expanded=true"
        expandSomeNodes(d) {
            // If node has expanded property set
            if (d.data.expanded) {
                // Retrieve node's parent
                let parent = d.parent;

                // While we can go up
                while (parent) {
                    // Expand all current parent's children
                    if (parent._children) {
                        parent.children = parent._children;
                    }

                    // Replace current parent holding object
                    parent = parent.parent;
                }
            }

            // Recursivelly do the same for collapsed nodes
            if (d._children) {
                d._children.forEach(ch => this.expandSomeNodes(ch));
            }

            // Recursivelly do the same for expanded nodes
            if (d.children) {
                d.children.forEach(ch => this.expandSomeNodes(ch));
            }
        },

        // Function which collapses passed node and it's descendants
        collapse(d) {
            if (d.children) {
                d._children = d.children;
                d._children.forEach(ch => this.collapse(ch));
                d.children = null;
            }
        },
        // Function which expands passed node and it's descendants
        expand(d) {
            if (d._children) {
                d.children = d._children;
                d.children.forEach(ch => this.expand(ch));
                d._children = null;
            }
        },

        zoomed(event) {
            // Reposition chart accordingly
            if (event.sourceEvent.type === "mousemove" || event.sourceEvent.type === "touchmove") {
                this.attrs.chart.attr(
                    "transform",
                    `translate(${event.transform.x}, ${event.transform.y})`,
                );
            }
        },

        // Generate custom diagonal
        // "Tree" path from: https://observablehq.com/@bumbeishvili/curved-edges?collection=@bumbeishvili/work-components
        diagonal(s, t) {
            // Source and target (s,t) coordinates
            const x = s.x;
            const y = s.y;
            const ex = t.x;
            const ey = t.y;

            // Calculate some variables based on source and target (s,t) coordinates
            const xrvs = ex - x < 0 ? -1 : 1;
            const yrvs = ey - y < 0 ? -1 : 1;
            const rdef = 35;
            const rInitial = Math.abs(ex - x) / 2 < rdef ? Math.abs(ex - x) / 2 : rdef;
            const r = Math.abs(ey - y) / 2 < rInitial ? Math.abs(ey - y) / 2 : rInitial;
            const h = Math.abs(ey - y) / 2 - r;
            const w = Math.abs(ex - x) - r * 2;

            if (s?.depth === 1) {
                // First layer of collapsible tree (horizontal)
                return `
                    M ${x} ${y}
                    L ${x} ${y + h * yrvs}
                    C  ${x} ${y + h * yrvs + r * yrvs} ${x} ${y + h * yrvs + r * yrvs} ${x +
                    r * xrvs} ${y + h * yrvs + r * yrvs}
                    L ${x + w * xrvs + r * xrvs} ${y + h * yrvs + r * yrvs}
                    C ${ex}  ${y + h * yrvs + r * yrvs} ${ex}  ${y +
                    h * yrvs +
                    r * yrvs} ${ex} ${ey - h * yrvs}
                    L ${ex} ${ey}
                `;
            }
            if (s?.depth === 2) {
                // First vertical layer
                return `
                    M ${ex} ${ey}
                    L ${ex} ${ey + 35}
                    C ${ex} ${ey + 50} ${ex} ${ey + 50} ${ex - 50} ${ey + 50}
                    L ${ex - 100} ${ey + 50}
                    C ${ex - 100 - 10} ${ey + 50} ${ex - 100 - 10} ${ey + 50} ${ex -
                    100 -
                    10} ${ey + 75}
                    L ${ex - 100 - 10} ${y - 10}
                    C ${ex - 100 - 10} ${y} ${ex - 100 - 10} ${y} ${ex - 100 - 10 + 10} ${y}
                    L ${x} ${y}
                `;
            }

            return `
                M ${ex - 95} ${ey}
                L ${ex - 95} ${y + r * yrvs}
                C ${ex - 95} ${y} ${ex - 95} ${y} ${ex - 95 + 10} ${y}
                L ${x} ${y}
            `;
        },
    },
};
</script>

<style scoped>
.l-org-chart >>> .node-button-g:hover .node-button-circle {
    fill: #828282;
}
.l-org-chart >>> .node-button-g:hover .node-button-text {
    fill: #fafafa;
}

.l-org-chart >>> .node-wrapper {
    color: #2a2a2a;
    background-image: linear-gradient(to bottom right, #fafafa, #fefefe);
    border-radius: 10px;
    display: flex;
}
.l-org-chart >>> .node-wrapper:hover {
    background-image: linear-gradient(to bottom right, #fafafa, #f8f8f8);
}
.l-org-chart >>> .node-wrapper.is-current-user {
    background-image: linear-gradient(to bottom right, #fafafa, #f8f8f8);
    cursor: default;
}
.l-org-chart >>> .node-wrapper.is-company {
    cursor: default;
}
.l-org-chart >>> .node-wrapper.is-company:hover {
    background-image: linear-gradient(to bottom right, #fafafa, #fefefe);
}

.l-org-chart >>> .node-wrapper .profile-picture {
    margin: 10px;
    border-radius: 100%;
}

.l-org-chart >>> .node-wrapper .profile-info {
    margin-top: 13px;
    color: #424242;
    line-height: 14px;
}
.l-org-chart >>> .node-wrapper .profile-info-name {
    font-size: 12px;
    font-weight: bold;
}
.l-org-chart >>> .node-wrapper .profile-info-title {
    margin-top: 3px;
    font-size: 12px;
}
</style>
